import { EditorView } from '@codemirror/view'
import type { Extension } from '@codemirror/state'
import RecordeApi from '~~/package/apis/recorde/RecordeApi'
import type PreSignedUrlParamsRequest from '~~/package/apis/recorde/request/PreSignedUrlParamsRequest'

interface KeystrokeEntry { t: number; op: 'ins' | 'del'; n: number; bs: boolean }

const FLUSH_INTERVAL_MS = 60_000

// TSK-11198 키스트로크 텔레메트리 — 응시(문서)당 하나.
//
// 응시 화면은 문항마다 다시 마운트된다. 페이지 인스턴스마다 이 서비스가 생기던 때는 이전 문항의 인스턴스가
// 60초 타이머를 계속 돌리며 자기 구간 이벤트만 담은 문서로 같은 S3 키(keystroke-events.json)를 덮어썼다 —
// 최종 파일이 어느 문항 구간인지가 타이머 순서에 달렸다. 그래서 웹캠 녹화와 같이 모듈 스코프에 하나만 둔다.
// INVARIANT: start/stop은 클라이언트 라이프사이클에서만 부른다(useWebcamRecordingService 참고).

export interface KeystrokeTelemetryService {
  /** CodeMirror에 붙이는 수집 extension. 문자 내용은 기록하지 않는다. */
  extension: () => Extension
  /** 같은 응시(env/domain/domain_id/user_uuid)를 이미 수집 중이면 이벤트를 그대로 이어 간다. */
  start: (params: PreSignedUrlParamsRequest) => void
  /** 타이머를 멈추고 마지막 문서를 올린다. */
  stop: () => Promise<void>
  flush: () => Promise<void>
  _recordChange: (change: { inserted: number; deleted: number }) => void
}

export const createKeystrokeTelemetryService = (): KeystrokeTelemetryService => {
  const recordeApi = new RecordeApi()
  let events: KeystrokeEntry[] = []
  let startedAtMs = 0
  let uploadParams: PreSignedUrlParamsRequest | null = null
  let sessionKey: string | null = null
  let flushTimer: ReturnType<typeof setInterval> | null = null

  // 문자 내용은 절대 기록하지 않는다 — 개수/타이밍/백스페이스 여부만.
  const _recordChange = ({ inserted, deleted }: { inserted: number; deleted: number }) => {
    const t = Date.now() - startedAtMs
    if (deleted > 0) events.push({ t, op: 'del', n: deleted, bs: inserted === 0 })
    if (inserted > 0) events.push({ t, op: 'ins', n: inserted, bs: false })
  }

  const extension = (): Extension =>
    EditorView.updateListener.of((update) => {
      if (!update.docChanged) return
      update.changes.iterChanges((fromA, toA, _fromB, _toB, ins) => {
        _recordChange({ inserted: ins.length, deleted: toA - fromA })
      })
    })

  // 여러 flush 호출(주기적 flush, stop()의 마지막 flush)이 동시에 진행되면
  // 먼저 시작했지만 늦게 끝나는 PUT이 더 최신(더 큰) 문서를 덮어써 데이터 유실이
  // 발생할 수 있다. flushChain으로 직렬화하여 한 번에 하나의 PUT만 진행되고
  // 호출 순서대로 완료되도록 보장한다.
  let flushChain: Promise<void> = Promise.resolve()

  const flush = (): Promise<void> => {
    // 호출 시점의 문서·대상을 고정한다. 대기 중에 다른 응시로 바뀌어도 이전 응시의 문서가 새 키로 가지 않는다.
    const params = uploadParams
    const doc = { v: 1, startedAtMs, events: events.slice() }
    flushChain = flushChain.then(async () => {
      if (!params || doc.events.length === 0) return
      try {
        const url = await recordeApi.getPreSignedUrl({ ...params, content_type: 'application/json' })
        await recordeApi.upload(url, new Blob([JSON.stringify(doc)], { type: 'application/json' }), 'application/json')
      } catch (e) {
        // 텔레메트리 실패는 응시를 방해하지 않는다
        console.warn('keystroke telemetry flush failed', e)
      }
    }).catch(() => {})
    return flushChain
  }

  const stop = async () => {
    if (flushTimer) { clearInterval(flushTimer); flushTimer = null }
    // flush()는 flushChain에 연결되므로, 이미 진행 중이거나 대기 중인 이전 flush가
    // 모두 끝난 뒤에 마지막 flush가 실행되고 그 완료까지 여기서 기다린다.
    await flush()
  }

  const start = (params: PreSignedUrlParamsRequest) => {
    const key = [params.env, params.domain, params.domain_id, params.user_uuid].join('/')
    if (flushTimer && key === sessionKey) return // 문항 이동으로 인한 재마운트 — 같은 수집을 이어 간다
    if (flushTimer) void stop() // 다른 응시 — 이전 응시의 마지막 문서를 올리고 새로 시작한다
    sessionKey = key
    uploadParams = params
    startedAtMs = Date.now()
    events = []
    flushTimer = setInterval(() => { void flush() }, FLUSH_INTERVAL_MS)
  }

  return { extension, start, stop, flush, _recordChange }
}

let shared: KeystrokeTelemetryService | null = null

export const useKeystrokeTelemetryService = (): KeystrokeTelemetryService => {
  shared ??= createKeystrokeTelemetryService()
  return shared
}
